import { ProjectInputFilter } from "domain/models/project-input-filter";
import Spinner from "presentation/components/Shared/Spinner";
import {
  useDeleteProjectMutation,
  useProjectsByFilter,
  useUpdateProjectStatusMutation,
} from "presentation/hooks/use-query-project";
import { useFilterProjectStore } from "presentation/stores/use-filter-project-store";
import { Column } from "primereact/column";
import {
  DataTable,
  DataTableFilterEvent,
  DataTableFilterMetaData,
  DataTablePageEvent,
  DataTableSortEvent,
} from "primereact/datatable";
import { Toast } from "primereact/toast";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import StatusProjectDropdown from "./StatusProjectDropdown";
import HeaderProjectGrid from "./HeaderProjectGrid";
import { Project } from "domain/entities/project";
import { useProjectStore } from "presentation/stores/use-project-store";
import { formatDateToPT } from "presentation/utils/formatDateToPT";
import { Tag } from "primereact/tag";

const ProjectGrid: React.FC = () => {
  const navigate = useNavigate();
  const { filters, setFilters } = useFilterProjectStore();
  const { project, setProject, resetProject } = useProjectStore();
  const deleteMutation = useDeleteProjectMutation();
  const toast = useRef<Toast>(null);
  const [lazyParams, setLazyParams] = useState({
    sortBy: "id",
    sortOrder: "desc",
    limit: 50,
    page: 1,
    filters: {},
  });
  const updateStatusMutation = useUpdateProjectStatusMutation();
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [statusProjectToUpdate, setStatusProjectToUpdate] =
    useState<Project | null>(null);
  const [statusJustification, setStatusJustification] = useState("");

  const {
    data: timesheets,
    isLoading,
    isError,
    error,
    refetch,
  } = useProjectsByFilter({
    sortBy: lazyParams.sortBy,
    sortOrder: lazyParams.sortOrder,
    page: lazyParams.page,
    limit: lazyParams.limit,
    ...lazyParams.filters,
  } as ProjectInputFilter);

  useEffect(() => {
    setLazyParams((prev) => ({
      ...prev,
      filters: Object.fromEntries(
        Object.entries(filters)
          .map(([key, value]) => {
            if ("constraints" in value) {
              return [key, value.constraints?.[0]?.value ?? ""];
            }
            return [key, (value as DataTableFilterMetaData)?.value ?? ""];
          })
          .filter(
            ([_, value]) =>
              value !== null && value !== undefined && value !== "",
          ),
      ),
    }));
  }, [filters]);

  useEffect(() => {
    refetch();
  }, [lazyParams.filters, refetch]);

  if (isLoading) return <Spinner />;

  if (isError && toast.current) {
    toast.current.show({
      severity: "error",
      summary: "Erro",
      detail: error?.message || "Ocorreu um erro inesperado",
      life: 1500,
    });
  }

  const handleStatusChange = () => {
    if (!statusProjectToUpdate || !statusJustification.trim()) {
      toast.current?.show({
        severity: "warn",
        summary: "Aviso",
        detail: "A justificativa é obrigatória.",
        life: 3000,
      });
      return;
    }

    const newStatus =
      statusProjectToUpdate.status === "Bloqueado"
        ? "Em progresso"
        : "Bloqueado";

    updateStatusMutation.mutate(
      {
        id: statusProjectToUpdate.id!,
        status: newStatus,
        justification: statusJustification,
      },
      {
        onSuccess: () => {
          toast.current?.show({
            severity: "success",
            summary: "Sucesso",
            detail: `Projeto ${newStatus === "Bloqueado" ? "inativado" : "ativado"} com sucesso`,
            life: 3000,
          });
          setShowStatusDialog(false);
          setStatusProjectToUpdate(null);
          setStatusJustification("");
        },
      },
    );
  };

  const showActionOptions = (rowData: Project) => {
    const isBlocked = rowData.status === "Bloqueado";
    return (
      <div className="flex gap-5 ">
        <i
          className={
            isBlocked
              ? "pi pi-play cursor-pointer text-green-500"
              : "pi pi-pause cursor-pointer text-orange-500"
          }
          title={isBlocked ? "Ativar Projeto" : "Inativar Projeto"}
          onClick={() => {
            setStatusProjectToUpdate(rowData);
            setStatusJustification("");
            setShowStatusDialog(true);
          }}
        />
        <i
          className="pi pi-pencil cursor-pointer"
          onClick={() => navigate(`/projects/edit/${rowData.id}`)}
        />
        {!rowData.hasAssociatedPOs && (
          <i
            className="pi pi-trash cursor-pointer"
            onClick={() => {
              confirmDialog({
                message: "Tem a certeza que deseja excluir este projeto?",
                header: "Confirmação",
                icon: "pi pi-info-circle",
                acceptClassName: "p-button-danger",
                acceptLabel: "Sim",
                rejectLabel: "Não",
                accept: () => {
                  deleteMutation.mutate(rowData, {
                    onSuccess: () => {
                      toast.current?.show({
                        severity: "success",
                        summary: "Sucesso",
                        detail: "Projeto excluído com sucesso",
                        life: 3000,
                      });
                    },
                  });
                },
              });
            }}
          />
        )}
      </div>
    );
  };

  return (
    <div>
      <DataTable
        value={timesheets?.data || []}
        lazy
        paginator
        first={(lazyParams.page - 1) * lazyParams.limit}
        rows={lazyParams.limit}
        totalRecords={timesheets?.pagination?.count || 0}
        loading={isLoading}
        onPage={(e: DataTablePageEvent) =>
          setLazyParams({
            ...lazyParams,
            page: e.page ? e.page + 1 : 1,
            limit: e.rows,
          })
        }
        sortField={lazyParams.sortBy}
        sortOrder={lazyParams.sortOrder === "asc" ? 1 : -1}
        onSort={(e: DataTableSortEvent) =>
          setLazyParams({
            ...lazyParams,
            sortBy: e.sortField,
            sortOrder:
              e.sortOrder === -1 ? "desc" : e.sortOrder === 1 ? "asc" : "",
          })
        }
        onFilter={(e: DataTableFilterEvent) => setFilters(e.filters)}
        filters={filters}
        size="small"
        resizableColumns
        columnResizeMode="expand"
        header={<HeaderProjectGrid />}
        rowsPerPageOptions={[2, 10, 25, 50]}
        paginatorLeft
        dataKey="id"
        globalFilterFields={["year", "month", "status"]}
      >
        <Column
          field="name"
          header="Nome"
          sortable
          filter
          showFilterMatchModes={false}
        />
        <Column
          field="client.name"
          header="Cliente"
          sortable
          filter
          showFilterMatchModes={false}
        />
        <Column
          field="startAt"
          header="Início"
          sortable
          filter
          showFilterMatchModes={false}
          body={(rowData) => formatDateToPT(rowData.startAt)}
        />
        <Column
          field="endAt"
          header="Término"
          sortable
          filter
          showFilterMatchModes={false}
          body={(rowData) => formatDateToPT(rowData.endAt)}
        />
        <Column
          header="Status"
          sortable
          filter
          filterField="status"
          body={(rowData) => {
            const status = rowData.status;
            let severity: "success" | "warning" | "danger" | "info" = "info";
            let customStyle: React.CSSProperties = {
              fontWeight: "600",
              fontSize: "0.85rem",
              padding: "0.25rem 0.6rem"
            };

            const lowerStatus = status?.toLowerCase() || "";

            if (lowerStatus === "em progresso") {
              severity = "info";
              customStyle = { ...customStyle, backgroundColor: "#ebf5fe", color: "#1e40af", border: "1px solid #bfdbfe" };
            } else if (lowerStatus === "concluído" || lowerStatus === "concluido") {
              severity = "success";
              customStyle = { ...customStyle, backgroundColor: "#eafaf1", color: "#27ae60", border: "1px solid #c2f0d1" };
            } else if (lowerStatus === "previsto") {
              severity = "warning";
              customStyle = { ...customStyle, backgroundColor: "#fff3e6", color: "#c26000", border: "1px solid #ffe0b2" };
            } else if (lowerStatus === "bloqueado" || lowerStatus === "inativo") {
              severity = "danger";
              customStyle = { ...customStyle, backgroundColor: "#fdf2f2", color: "#c81e1e", border: "1px solid #fde8e8" };
            } else {
              severity = "info";
              customStyle = { ...customStyle, backgroundColor: "#f3f4f6", color: "#374151", border: "1px solid #e5e7eb" };
            }

            return <Tag value={status} severity={severity} rounded style={customStyle} />;
          }}
          showFilterMatchModes={false}
          filterElement={(options) => (
            <StatusProjectDropdown options={options} />
          )}
        />

        <Column
          body={showActionOptions}
          header="Ações"
          align="left"
          alignHeader="left"
        />
      </DataTable>
      <ConfirmDialog />
      <Toast ref={toast} position="bottom-right" />

      <Dialog
        header={
          statusProjectToUpdate?.status === "Bloqueado"
            ? "Ativar Projeto"
            : "Inativar Projeto"
        }
        visible={showStatusDialog}
        style={{ width: "30vw" }}
        onHide={() => setShowStatusDialog(false)}
        footer={
          <div>
            <Button
              label="Cancelar"
              icon="pi pi-times"
              onClick={() => setShowStatusDialog(false)}
              className="p-button-text"
            />
            <Button
              label="Guardar"
              icon="pi pi-check"
              onClick={handleStatusChange}
              autoFocus
              loading={updateStatusMutation.isPending}
            />
          </div>
        }
      >
        <div className="default-grid">
          <div className="default-4-cols">
            <label htmlFor="justification">
              Justificativa para a alteração de status:
            </label>
            <InputTextarea
              id="justification"
              value={statusJustification}
              onChange={(e) => setStatusJustification(e.target.value)}
              rows={5}
              className="w-full"
              autoFocus
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default ProjectGrid;
